<script lang="ts">
  // The ring's role comes from the HOST: both apps keep their own role map because
  // they load it from different routes, and neither map belongs in a shared package.
  import { ui } from './host';
  import { quadMeta, isQuadrant } from './quadrant';
  import RoleIcon from './RoleIcon.svelte';

  // Profile picture with an initial-letter fallback. Sizes are fixed px so they
  // stay crisp and align in dense lists. When `memberId` is given, the avatar
  // wears a thick ring in the member's current contributor-role colour (from the
  // per-community roles map — see lib/roles), plus a small corner badge with the
  // role's icon (white glyph on the role-coloured circle) on large-enough avatars.
  let {
    src = null,
    name = '',
    size = 28,
    memberId = null,
  }: { src?: string | null; name?: string; size?: number; memberId?: number | null | undefined } = $props();

  const initial = $derived((name.trim()[0] ?? '?').toUpperCase());

  const role = $derived(memberId != null ? ui().roleOf(memberId) : null);
  const meta = $derived(role && isQuadrant(role) ? quadMeta(role) : null);
  // Ring thickness scales with the avatar, clamped so it always reads as a stroke.
  const ringW = $derived(Math.min(4, Math.max(2, Math.round(size * 0.1))));
  const ring = $derived(meta ? `box-shadow:0 0 0 ${ringW}px ${meta.color}` : '');
  // Corner badge — only on avatars big enough for the glyph to read (skips the
  // tiny 20px reply-list avatars, which keep just the ring).
  const showBadge = $derived(meta != null && size >= 24);
  const badge = $derived(Math.max(13, Math.round(size * 0.44)));
</script>

<!-- `.redact` goes on the FACE and on the initial-letter fallback, never on this
     wrapper: the role badge below is a product concept, not an identifying value, and
     it stays sharp. (A `filter` on the wrapper would rasterise the badge with it and
     there is no way to except a child — see lib/privacy.svelte.ts.) The initial is
     marked because it is the first letter of a real name. -->
<span class="relative inline-flex shrink-0 {memberId != null ? 'cursor-pointer' : ''}" style="width:{size}px;height:{size}px">
  {#if src}
    <img
      {src}
      alt=""
      class="redact rounded-full object-cover"
      style="width:{size}px;height:{size}px;{ring}"
      loading="lazy"
    />
  {:else}
    <span
      class="redact flex items-center justify-center rounded-full bg-base-300 font-medium text-base-content/70"
      style="width:{size}px;height:{size}px;font-size:{Math.round(size * 0.42)}px;{ring}"
      aria-hidden="true">{initial}</span
    >
  {/if}
  {#if showBadge && meta}
    <span
      class="absolute flex items-center justify-center rounded-full text-white"
      style="width:{badge}px;height:{badge}px;right:{-badge * 0.15}px;bottom:{-badge * 0.15}px;background:{meta.color};box-shadow:0 0 0 1.5px var(--color-base-100)"
      aria-hidden="true"
    >
      <RoleIcon name={meta.icon} size={Math.round(badge * 0.62)} />
    </span>
  {/if}
</span>
